import { dpApiV2, normalizeData } from "./apiV2";
import charitiesApi from "./charities";

export type Category = {
  id: number;
  name: string;
}

export type NormalisedCategories = {
  ids: number[],
  byId: {
    [key: number]: Category
  }
}

export type CharityCategory = {
  id: number;
  name: string;
  charity: number;
}

const extendedApi = dpApiV2.injectEndpoints({
  endpoints: builder => ({
    getActiveCategories: builder.query<string[], string>({
      query: () => ({
        url: "categories/charity-categories/public/active/",
      }),
      providesTags: [ "Category" ]
    }),
    getAllCategories: builder.query<NormalisedCategories, string>({
      query: () => ({
        url: "categories/public/",
        responseHandler: async response => {
          if (response.ok) {
            const responseJson = await response.json();
            return normalizeData<Category>(responseJson);
          }
          // Just return the error data
          return await response.json();
        }
      }),
      providesTags: [ "Category" ]
    }),
    createCharityCategory: builder.mutation<CharityCategory, Omit<CharityCategory, "id">>({
      query: body => ({
        url: "categories/charity-categories/",
        method: "POST",
        body
      }),
      invalidatesTags: [ "Charity" ],
      async onQueryStarted(
        { ...patch },
        { dispatch, queryFulfilled, getState }
      ) {
        const patchResults = [];
        for (const {
          endpointName,
          originalArgs
        } of extendedApi.util.selectInvalidatedBy(getState(), [
            { type: "Charity" }
          ])) {
          if (![ "getCharityByOwnerId", "getCharityById" ].includes(endpointName)) continue;
          const patchResult = dispatch(
            charitiesApi.util.updateQueryData(
              endpointName as ("getCharityByOwnerId" | "getCharityById"),
              originalArgs,
              draft => {
                const mockId = 1e10 + Math.random();
                draft.categories = [ ...draft.categories, { ...patch, id: mockId } ];
              }
            )
          );
          patchResults.push(patchResult);
        }
        try {
          await queryFulfilled;
        } catch {
          for (const patchResult of patchResults) {
            patchResult.undo();
          }
        }
      }
    }),
    deleteCharityCategory: builder.mutation<null, number>({
      query: id => ({
        url: `categories/charity-categories/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: [ "Charity" ],
      async onQueryStarted(
        id,
        { dispatch, queryFulfilled, getState }
      ) {
        const patchResults = [];
        for (const {
          endpointName,
          originalArgs
        } of extendedApi.util.selectInvalidatedBy(getState(), [
            { type: "Charity" }
          ])) {
          if (![ "getCharityByOwnerId", "getCharityById" ].includes(endpointName)) continue;
          const patchResult = dispatch(
            charitiesApi.util.updateQueryData(
              endpointName as ("getCharityByOwnerId" | "getCharityById"),
              originalArgs,
              draft => {
                draft.categories = [ ...draft.categories.filter(cat => cat.id !== id) ];
              }
            )
          );
          patchResults.push(patchResult);
        }
        try {
          await queryFulfilled;
        } catch {
          for (const patchResult of patchResults) {
            patchResult.undo();
          }
        }
      }
    }),
  }),
  overrideExisting: true
});

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
  useGetActiveCategoriesQuery,
  useGetAllCategoriesQuery,
  useCreateCharityCategoryMutation,
  useDeleteCharityCategoryMutation,
} = extendedApi;
